import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";

/** Distance (px) the pointer must travel during a drag before the gesture is
 *  treated as a pan rather than a click — lets consumers suppress the trailing
 *  click so a pan never registers as a point/selection. */
const PAN_CLICK_THRESHOLD_PX = 3;

const DEFAULT_MIN_ZOOM = 0.3;
const DEFAULT_MAX_ZOOM = 5;
const DEFAULT_ZOOM_STEP = 0.3;
const WHEEL_LISTENER_OPTIONS = { passive: false, capture: true } as const;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

interface ViewportSize {
  width: number;
  height: number;
}

function positiveViewportSize(width: number, height: number): ViewportSize | null {
  return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
    ? { width, height }
    : null;
}

function readViewportSize(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  const style = getComputedStyle(element);
  const inlineInsets =
    Number.parseFloat(style.borderLeftWidth || "0") +
    Number.parseFloat(style.borderRightWidth || "0") +
    Number.parseFloat(style.paddingLeft || "0") +
    Number.parseFloat(style.paddingRight || "0");
  const blockInsets =
    Number.parseFloat(style.borderTopWidth || "0") +
    Number.parseFloat(style.borderBottomWidth || "0") +
    Number.parseFloat(style.paddingTop || "0") +
    Number.parseFloat(style.paddingBottom || "0");
  return positiveViewportSize(rect.width - inlineInsets, rect.height - blockInsets);
}

export interface UseZoomPanTransformOptions {
  /** Freeze interaction while keeping the current transform mounted. Default
   *  false. When true the hook ignores Ctrl+wheel / Ctrl+drag and does not call
   *  preventDefault (browser zoom passes through) — for transiently locking a
   *  surface without unmounting it or losing its zoom/pan. */
  locked?: boolean;
  /** Minimum zoom factor. Default 0.3. */
  minZoom?: number;
  /** Maximum zoom factor. Default 5. */
  maxZoom?: number;
  /** Zoom delta applied per wheel notch. Default 0.3. */
  zoomStep?: number;
  /** Cursor shown when the modifier is not held and no pan is active.
   *  Default "default" — a point-placing consumer can pass "crosshair". */
  idleCursor?: CSSProperties["cursor"];
  /**
   * Rebase pixel pan per axis when the positive container size changes, preserving the same
   * relative content view across presentation hosts. Default false, retaining the historical
   * pixel-coordinate behavior for annotation and other direct hook consumers.
   */
  preserveRelativeViewOnResize?: boolean;
}

export interface ZoomPanTransformApi {
  /** Current zoom factor (1 = fit). */
  zoom: number;
  /** Current pan offset on X in viewport px. It may rebase when the resize option is enabled. */
  panX: number;
  /** Current pan offset on Y in viewport px. It may rebase when the resize option is enabled. */
  panY: number;
  /** True while a pan drag is in progress. */
  isPanning: boolean;
  /** True while the Ctrl modifier is held (zoom/pan armed). */
  isModifierPressed: boolean;
  /** Cursor reflecting the current interaction state. */
  interactionCursor: NonNullable<CSSProperties["cursor"]>;
  /** Style to spread onto the transformed surface element. */
  transformStyle: CSSProperties;
  /** Ref callback for the container. The hook attaches a capture-phase,
   *  non-passive `wheel` listener here so Ctrl+wheel can `preventDefault()`
   *  before media children handle it, and uses the element's bounds to anchor
   *  pointer-relative zoom. With `preserveRelativeViewOnResize`, a ResizeObserver on the same
   *  container rebases pixel pan when its positive viewport size changes. */
  containerRef: (element: HTMLElement | null) => void;
  /** Wire to the container's `onPointerDown`. Ctrl + drag pans. */
  handlePointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  /** Returns (and clears) whether the last gesture was a pan — call from a
   *  click handler to ignore clicks that conclude a pan drag. */
  consumeSuppressedClick: () => boolean;
  /** Whether another configured zoom step can be applied. */
  canZoomIn: boolean;
  canZoomOut: boolean;
  /** Zoom one configured step around the viewport center. */
  zoomIn: () => void;
  zoomOut: () => void;
  /** Reset zoom/pan back to the identity transform. */
  reset: () => void;
}

/**
 * Headless zoom/pan engine. Owns the zoom/pan state and the Ctrl+wheel /
 * Ctrl+drag gesture math, but renders nothing — the consumer applies
 * `transformStyle` to its surface and attaches `containerRef`. Powers
 * `ZoomPanWrapper` (plain viewing) and point-placing annotation canvases
 * (which additionally read pointer coordinates). Consumers that move the same
 * transformed surface between differently sized hosts can opt into positive-size
 * pan rebasing with `preserveRelativeViewOnResize`.
 *
 * The wheel listener is attached natively with `{ passive: false, capture: true }`
 * so the Ctrl+wheel gesture can suppress the browser's own page zoom before
 * media children handle it — a React `onWheel` handler runs passively and cannot.
 */
export function useZoomPanTransform(options: UseZoomPanTransformOptions = {}): ZoomPanTransformApi {
  const {
    locked = false,
    minZoom = DEFAULT_MIN_ZOOM,
    maxZoom = DEFAULT_MAX_ZOOM,
    zoomStep = DEFAULT_ZOOM_STEP,
    idleCursor = "default",
    preserveRelativeViewOnResize = false,
  } = options;

  // zoom/pan live in ONE state object so the wheel handler can derive the
  // pointer-anchored pan from the same `prev` in a single pure updater.
  // Splitting them forces a nested setState inside the zoom updater — a side
  // effect StrictMode double-invokes, over-applying the anchor compensation
  // so content drifts away from the cursor on every wheel notch.
  const [transform, setTransform] = useState({ zoom: 1, panX: 0, panY: 0 });
  const { zoom, panX, panY } = transform;
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const [isPanning, setIsPanning] = useState(false);
  const [isViewportRebasing, setIsViewportRebasing] = useState(false);
  const [isModifierPressed, setIsModifierPressed] = useState(false);

  // Latest gesture config, read by the stable native wheel/pointer handlers.
  const configRef = useRef({ locked, minZoom, maxZoom, zoomStep });
  configRef.current = { locked, minZoom, maxZoom, zoomStep };

  const containerElRef = useRef<HTMLElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const viewportSizeRef = useRef<ViewportSize | null>(null);
  const dragOriginRef = useRef<{
    clientX: number;
    clientY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const transformStyle = useMemo<CSSProperties>(
    () => ({
      transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
      transformOrigin: "0 0",
      transition: isPanning || isViewportRebasing ? "none" : "transform 0.1s ease-out",
    }),
    [isPanning, isViewportRebasing, panX, panY, zoom],
  );

  const rebaseViewport = useCallback((nextSize: ViewportSize) => {
    const previousSize = viewportSizeRef.current;
    viewportSizeRef.current = nextSize;
    if (!previousSize) return;

    const scaleX = nextSize.width / previousSize.width;
    const scaleY = nextSize.height / previousSize.height;
    if (scaleX === 1 && scaleY === 1) return;

    const current = transformRef.current;
    if (current.panX === 0 && current.panY === 0) return;

    // pan is public px state, but its visual meaning is relative to the viewport. Scaling each
    // axis keeps every normalized content point at the same normalized viewport coordinate.
    setIsViewportRebasing(true);
    setTransform((prev) => ({
      ...prev,
      panX: prev.panX * scaleX,
      panY: prev.panY * scaleY,
    }));

    const dragOrigin = dragOriginRef.current;
    if (dragOrigin) {
      dragOriginRef.current = {
        ...dragOrigin,
        panX: dragOrigin.panX * scaleX,
        panY: dragOrigin.panY * scaleY,
      };
    }
  }, []);

  const zoomAt = useCallback((direction: -1 | 1, clientPoint?: { x: number; y: number }) => {
    const { locked: isLocked, minZoom: min, maxZoom: max, zoomStep: step } = configRef.current;
    if (isLocked) return;

    const container = containerElRef.current;
    const rect = container?.getBoundingClientRect();
    const pointerX = rect ? (clientPoint ? clientPoint.x - rect.left : rect.width / 2) : 0;
    const pointerY = rect ? (clientPoint ? clientPoint.y - rect.top : rect.height / 2) : 0;

    setTransform((prev) => {
      const nextZoom = clamp(prev.zoom + direction * step, min, max);
      if (nextZoom === prev.zoom) return prev;
      if (!rect) return { ...prev, zoom: nextZoom };

      const originX = (pointerX - prev.panX) / prev.zoom;
      const originY = (pointerY - prev.panY) / prev.zoom;
      return {
        zoom: nextZoom,
        panX: pointerX - originX * nextZoom,
        panY: pointerY - originY * nextZoom,
      };
    });
  }, []);

  // Stable native wheel listener — attached capture-phase with passive:false so
  // preventDefault actually stops browser page zoom and media child handling.
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      const { locked: isLocked } = configRef.current;
      if (isLocked || !event.ctrlKey) return;
      event.preventDefault();
      const direction = event.deltaY > 0 ? -1 : 1;
      zoomAt(direction, { x: event.clientX, y: event.clientY });
    },
    [zoomAt],
  );

  const containerRef = useCallback(
    (element: HTMLElement | null) => {
      const previous = containerElRef.current;
      if (previous === element) return;

      if (previous && previous !== element) {
        previous.removeEventListener("wheel", handleWheel, WHEEL_LISTENER_OPTIONS);
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      viewportSizeRef.current = null;
      containerElRef.current = element;
      if (element) {
        element.addEventListener("wheel", handleWheel, WHEEL_LISTENER_OPTIONS);
        if (!preserveRelativeViewOnResize) return;

        viewportSizeRef.current = readViewportSize(element);

        if (typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver((entries) => {
            if (containerElRef.current !== element || resizeObserverRef.current !== observer) {
              return;
            }
            const entry = entries.find((candidate) => candidate.target === element);
            const nextSize = entry
              ? positiveViewportSize(entry.contentRect.width, entry.contentRect.height)
              : readViewportSize(element);
            // A presentation move or hidden tile may expose a transient 0×0 box. Keep the last
            // positive baseline so the next visible geometry rebases from the real prior view.
            if (nextSize) rebaseViewport(nextSize);
          });
          resizeObserverRef.current = observer;
          observer.observe(element);
        }
      }
    },
    [handleWheel, preserveRelativeViewOnResize, rebaseViewport],
  );

  useEffect(
    () => () => {
      containerElRef.current?.removeEventListener("wheel", handleWheel, WHEEL_LISTENER_OPTIONS);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      viewportSizeRef.current = null;
    },
    [handleWheel],
  );

  useEffect(() => {
    if (!isViewportRebasing) return;
    const frame = requestAnimationFrame(() => setIsViewportRebasing(false));
    return () => cancelAnimationFrame(frame);
  }, [isViewportRebasing, panX, panY]);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (configRef.current.locked || !event.ctrlKey || event.button !== 0) return;

      dragOriginRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
        panX,
        panY,
      };
      suppressClickRef.current = false;
      setIsPanning(true);
    },
    [panX, panY],
  );

  useEffect(() => {
    const syncModifierState = (event: KeyboardEvent) => {
      setIsModifierPressed(event.ctrlKey);
    };
    const clearModifierState = () => setIsModifierPressed(false);

    window.addEventListener("keydown", syncModifierState);
    window.addEventListener("keyup", syncModifierState);
    window.addEventListener("blur", clearModifierState);

    return () => {
      window.removeEventListener("keydown", syncModifierState);
      window.removeEventListener("keyup", syncModifierState);
      window.removeEventListener("blur", clearModifierState);
    };
  }, []);

  useEffect(() => {
    if (!isPanning) return;

    let frame = 0;
    let latest: { clientX: number; clientY: number } | null = null;

    const applyLatest = () => {
      frame = 0;
      const origin = dragOriginRef.current;
      const pointer = latest;
      if (!origin || !pointer) return;
      setTransform((prev) => ({
        ...prev,
        panX: origin.panX + (pointer.clientX - origin.clientX),
        panY: origin.panY + (pointer.clientY - origin.clientY),
      }));
    };

    const handlePointerMove = (event: PointerEvent) => {
      const origin = dragOriginRef.current;
      if (!origin) return;

      if (
        Math.abs(event.clientX - origin.clientX) > PAN_CLICK_THRESHOLD_PX ||
        Math.abs(event.clientY - origin.clientY) > PAN_CLICK_THRESHOLD_PX
      ) {
        suppressClickRef.current = true;
      }
      // 고폴링 마우스는 pointermove가 프레임 레이트를 웃돈다(수백 Hz) — 프레임당 1회만 커밋.
      latest = { clientX: event.clientX, clientY: event.clientY };
      if (typeof requestAnimationFrame !== "function") {
        applyLatest();
        return;
      }
      if (frame === 0) frame = requestAnimationFrame(applyLatest);
    };

    const handlePointerUp = () => {
      // 예약된 프레임을 flush해 드래그의 마지막 이동을 잃지 않는다.
      if (frame !== 0 && typeof cancelAnimationFrame === "function") cancelAnimationFrame(frame);
      applyLatest();
      dragOriginRef.current = null;
      setIsPanning(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      if (frame !== 0 && typeof cancelAnimationFrame === "function") cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isPanning]);

  const consumeSuppressedClick = useCallback(() => {
    const suppressed = suppressClickRef.current;
    suppressClickRef.current = false;
    return suppressed;
  }, []);

  const zoomIn = useCallback(() => zoomAt(1), [zoomAt]);
  const zoomOut = useCallback(() => zoomAt(-1), [zoomAt]);

  const reset = useCallback(() => {
    setTransform({ zoom: 1, panX: 0, panY: 0 });
    dragOriginRef.current = null;
    suppressClickRef.current = false;
    setIsPanning(false);
  }, []);

  const interactionCursor: NonNullable<CSSProperties["cursor"]> = isPanning
    ? "grabbing"
    : !locked && isModifierPressed
      ? "grab"
      : (idleCursor ?? "default");

  return {
    zoom,
    panX,
    panY,
    isPanning,
    isModifierPressed,
    interactionCursor,
    transformStyle,
    containerRef,
    handlePointerDown,
    consumeSuppressedClick,
    canZoomIn: !locked && zoom < maxZoom,
    canZoomOut: !locked && zoom > minZoom,
    zoomIn,
    zoomOut,
    reset,
  };
}
